import { format } from 'date-fns';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { ListUtils } from '../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { usePostAssignWorkInfoDaySearch } from '../../../hooks/queries/assignWorkInfo';
import useToastHook from '../../../hooks/ToastHook';
import { AssignWorkInfoDaySearchResult } from '../../../models/gen/assignWorkInfoDaySearchResult';
import { AssignWorkInfoDaySearchResultResults } from '../../../models/gen/assignWorkInfoDaySearchResultResults';
import { Formatter } from '../../../utils/FormatUtils';
import Wireframe from '../../wireframe/Wireframe';

/**
 * 就業内容画面パラメータ
 */
export type WorkContentParams = {
  /**
   * 就業日
   */
  date: string;
  /**
   * ユーザコード
   */
  usercode: string;
};

export type WorkContentOutletContext = {
  data?: AssignWorkInfoDaySearchResult;
  isLoading?: boolean;
  matterIndex?: number;
  extraFeesConcatenated?: Array<number>;
};

export type AssignWorkInfoDaySearchResultForm =
  AssignWorkInfoDaySearchResult & {
    extraFees?: Array<number>;
  };

/**
 * 就業内容画面
 *
 * - URL: `/work-content/:date/:usercode/{mode}?outsourcerCode=&index=`
 *   - mode: edit / view [default]
 * - パラメータ
 *   - date: 日付
 *   - usercode: ユーザコード
 * - クエリ
 *   - outsourcerCode: 外注先コード
 *   - index: 現場インデックス
 *
 * @returns
 */
const WorkContent: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const params = useParams<WorkContentParams>();
  const [searchParams] = useSearchParams();
  const { toast } = useToastHook();
  const form = useForm<AssignWorkInfoDaySearchResultForm>();
  const { data, error, isError, isLoading, refetch } =
    usePostAssignWorkInfoDaySearch({
      date: params.date!,
      userCode: params.usercode!,
      outsourcerCode: searchParams.get('outsourcerCode') || undefined,
    });
  React.useState(() => refetch());
  const convertedData = React.useMemo(
    () => ({
      ...data,
      results: data?.results
        ?.sort((a, b) =>
          a.assignedWorkerWorkInfo.workTimeFrom !==
          b.assignedWorkerWorkInfo.workTimeFrom
            ? ListUtils.sort<AssignWorkInfoDaySearchResultResults>(
                (result) => result.assignedWorkerWorkInfo.workTimeFrom
              )(a, b)
            : a.matterSummary.requestTime !== b.matterSummary.requestTime
            ? ListUtils.sort<AssignWorkInfoDaySearchResultResults>(
                (result) => result.assignedWorkerSummary.requestTime
              )(a, b)
            : ListUtils.sort<AssignWorkInfoDaySearchResultResults>(
                (result) => result.assignedWorkerSummary.assignWorkerId
              )(a, b)
        )
        .map((result) => ({
          ...result,
          assignedWorkerWorkInfo: {
            ...result.assignedWorkerWorkInfo,
            workTimeFrom: Formatter.dateTimeString(
              TypeUtils.isNilString(result.assignedWorkerWorkInfo.workTimeFrom)
                ? `${params.date} 08:00:00`
                : result.assignedWorkerWorkInfo.workTimeFrom
            ),
            workTimeTo: Formatter.dateTimeString(
              TypeUtils.isNilString(result.assignedWorkerWorkInfo.workTimeTo)
                ? `${params.date} 17:00:00`
                : result.assignedWorkerWorkInfo.workTimeTo
            ),
            overtimeApprovedTime: Formatter.dateTimeString(
              TypeUtils.isNilString(
                result.assignedWorkerWorkInfo.overtimeApprovedTime
              )
                ? `${params.date} 12:00:00`
                : result.assignedWorkerWorkInfo.overtimeApprovedTime
            ),
            restTime:
              TypeUtils.isNilString(
                result.assignedWorkerWorkInfo.workTimeFrom
              ) ||
              TypeUtils.isNilString(result.assignedWorkerWorkInfo.workTimeTo)
                ? 60
                : result.assignedWorkerWorkInfo.restTime,
          },
        })),
      extraFees:
        data?.results
          ?.flatMap((result) => result.extraFeeIds)
          .filter((extraFeeId) => !TypeUtils.isNil(extraFeeId)) || [],
    }),
    [data]
  );

  React.useEffect(() => {
    if (!isLoading) {
      form.reset(convertedData);
    }
  }, [isLoading, data?.results]);
  React.useEffect(() => {
    if (isError) {
      toast(error instanceof AppError ? error : '就業内容の取得に失敗しました');
    }
  }, [isError]);

  const index = searchParams.get('index');

  return (
    <Wireframe isLoaded={!isLoading} title={pageNames.WORK_CONTENT}>
      <FormProvider {...form}>
        <Outlet
          context={{
            data,
            isLoading,
            matterIndex:
              TypeUtils.isNilString(index) ||
              (Array.isArray(data?.results)
                ? data!.results!.length - 1 < parseInt(index)
                : true)
                ? undefined
                : parseInt(index),
            extraFeesConcatenated:
              data?.results
                ?.flatMap((result) => result.extraFeeIds)
                .filter((extraFeeId) => !TypeUtils.isNilNumber(extraFeeId)) ||
              [],
          }}
        />
      </FormProvider>
    </Wireframe>
  );
};

export default WorkContent;
